import { useState } from 'react'
import LogInput from './components/LogInput'
import AnalysisResults from './components/AnalysisResults'
import ThemeToggle from './components/ThemeToggle'
import type { AnalysisResult } from './types'

function App() {
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)

  const handleAnalysisComplete = (result: AnalysisResult) => {
    setAnalysisResult(result)
    setIsAnalyzing(false)
  }

  const handleAnalysisStart = () => {
    setIsAnalyzing(true)
    setAnalysisResult(null)
  }

  const handleReset = () => {
    setAnalysisResult(null)
    setIsAnalyzing(false)
  }

  const handleAnalysisError = () => {
    setIsAnalyzing(false)
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200 flex flex-col">
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
              <img 
                src="/cloudots-logo-favicon.png" 
                alt="Cloudots Logo" 
                className="h-8 sm:h-12 w-auto flex-shrink-0"
              />
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white truncate">Cloudots</h1>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 hidden sm:block">Advanced Cloud Telemetry Analysis</p>
              </div>
            </div>
            <div className="flex-shrink-0 flex items-center space-x-3">
              <a
                href="https://kb.cloudots.io"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 text-sm font-medium text-gray-700 dark:text-gray-300"
                aria-label="Visit Cloudots Knowledgebase"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <span className="hidden sm:inline">Knowledgebase</span>
              </a>
              <ThemeToggle />
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full" role="main">
        <section className="mb-8" aria-labelledby="main-heading">
          <h2 id="main-heading" className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Cloud Log Security Analysis
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Paste your cloud telemetry logs below to analyze them for security threats, 
            misconfigurations, and suspicious activities. Get detailed severity scoring.
          </p>
        </section>

        <section className="space-y-8" aria-labelledby="analysis-section">
          <h3 id="analysis-section" className="sr-only">Log Analysis Interface</h3>
          
          <article aria-labelledby="log-input-heading">
            <h4 id="log-input-heading" className="sr-only">Log Input and Upload</h4>
            <LogInput
              onAnalysisStart={handleAnalysisStart}
              onAnalysisComplete={handleAnalysisComplete}
              onAnalysisError={handleAnalysisError}
              isAnalyzing={isAnalyzing}
              onReset={handleReset}
              hasResults={!!analysisResult}
            />
          </article>

          {analysisResult && (
            <article aria-labelledby="analysis-results-heading">
              <h4 id="analysis-results-heading" className="sr-only">Security Analysis Results</h4>
              <AnalysisResults result={analysisResult} />
            </article>
          )}
        </section>
      </main>

      <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center space-y-2">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Cloudots - Analyze your cloud logs for security threats
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Powered with 🧡 by Brava
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default App